import { useMemo, type ReactNode } from 'react';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  civilToday,
  exportQueryOf,
  formatCivilDate,
  links,
  resolveFormattingLocale,
} from '@eis/core';
import {
  ExportButton,
  InfiniteDataGrid,
  useGridExportColumns,
  usePermissions,
  type ExportableGridColDef,
  type InfiniteGridList,
} from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import { isPastValidity, moneyLabel, toLeadRows, type LeadRow } from '../model/leads';
import type { Lead } from '../model/types';
import { LeadStatusChip } from './LeadStatusChip';

/**
 * The lead grid (SDD-013 §8.1.1), shared by the standalone list and the group policy's Leads tab
 * so the two cannot spell a column differently. Columns: code · status (the tenant's name,
 * coloured by kind) · tariff (a product name, never a version) · partner (the policyholder resolved
 * through the directory, or the draft's name marked "not registered") · premium · start · valid
 * until (past dates marked) · updated · the policy once the lead became one (R-4).
 *
 * With `exportAs` the grid carries "Exportieren" (SDD-020 §9): each column names its path in the
 * `Lead` message, and the export is the list's own query.
 */

/** What the export of this grid asks for: the list's query, and the group policy of a tab. */
export interface LeadExport {
  readonly label: string;
  readonly parent?: string;
  readonly filters?: string;
  readonly search?: string;
  readonly order?: string;
}
/** The lead catalog's sortable keys, by column. */
const LEAD_SORT_KEYS: Readonly<Record<string, string>> = {
  code: 'code',
  statusName: 'statusCode',
  tarif: 'tarifSlug',
};

export function LeadGrid({
  leads,
  list,
  emptyMessage,
  showParent,
  order,
  onOrderChange,
  exportAs,
}: {
  /** Every loaded lead (the list's `items`). */
  readonly leads: readonly Lead[];
  /** The infinite list the rows come from (SDD-017 §9): paging, loading, errors, retry. */
  readonly list: InfiniteGridList;
  readonly emptyMessage: string;
  /** The standalone list shows a member's master as a link (R-2 from the other direction). */
  readonly showParent: boolean;
  readonly order?: string;
  readonly onOrderChange?: (order: string) => void;
  readonly exportAs?: LeadExport;
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canSeePolicies = can(p.policies.view);
  const canSeeGroupPolicies = can(p.groupPolicies.view);
  const rows = useMemo(() => toLeadRows(leads), [leads]);
  const today = civilToday();

  const columns = useMemo<ExportableGridColDef<LeadRow>[]>(
    () => [
      { field: 'code', headerName: t('leads.list.code'), width: 150, exportPath: 'code' },
      {
        field: 'statusName',
        headerName: t('leads.list.status'),
        width: 170,
        exportPath: 'status.name',
        renderCell: ({ row }) => (
          <LeadStatusChip
            status={{
              code: row.statusCode,
              name: row.statusName,
              kind: `STATUS_KIND_${row.kind}` as never,
            }}
          />
        ),
      },
      {
        field: 'tarif',
        headerName: t('leads.list.tarif'),
        width: 120,
        // The service's derived columns resolve these as this grid does (SDD-020 §14).
        exportPath: 'tarifName',
      },
      {
        field: 'partner',
        headerName: t('leads.list.partner'),
        flex: 1,
        minWidth: 200,
        exportPath: 'partnerName',
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <span>{row.partner === '' ? '—' : row.partner}</span>
            {row.partnerIsDraft && (
              <Tooltip title={t('leads.list.notRegisteredHint')}>
                <Chip size="small" variant="outlined" label={t('leads.list.notRegistered')} />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      ...(showParent
        ? [
            {
              field: 'parentPolicyCode',
              headerName: t('leads.list.parent'),
              width: 140,
              exportPath: 'draft.parentPolicyCode',
              renderCell: ({ row }: { row: LeadRow }): ReactNode =>
                row.parentPolicyCode === '' ? (
                  '—'
                ) : canSeeGroupPolicies ? (
                  <Link
                    href={links.insurance.groupPolicy(row.parentPolicyCode, 'leads')}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      void navigate({
                        href: links.insurance.groupPolicy(row.parentPolicyCode, 'leads'),
                      });
                    }}
                  >
                    {row.parentPolicyCode}
                  </Link>
                ) : (
                  row.parentPolicyCode
                ),
            } satisfies ExportableGridColDef<LeadRow>,
          ]
        : []),
      {
        field: 'monthlyPremium',
        headerName: t('leads.list.premium'),
        width: 130,
        exportPath: 'draft.premium.monthly',
        exportType: 'MONEY',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_value, row) =>
          row.monthlyPremium === ''
            ? ''
            : moneyLabel({ amount: row.monthlyPremium, currency: row.currency }, locale),
      },
      {
        field: 'effectiveFrom',
        headerName: t('leads.list.start'),
        width: 120,
        exportPath: 'effectiveFrom',
        exportType: 'DATE',
        valueFormatter: (value: string) => (value === '' ? '—' : formatCivilDate(value, locale)),
      },
      {
        field: 'validUntil',
        headerName: t('leads.list.validUntil'),
        width: 150,
        exportPath: 'validUntil',
        exportType: 'DATE',
        renderCell: ({ row }) =>
          row.validUntil === '' ? (
            <Typography variant="body2" color="text.secondary">
              {t('leads.list.noValidity')}
            </Typography>
          ) : (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <span>{formatCivilDate(row.validUntil, locale)}</span>
              {isPastValidity(row.validUntil, today) && row.kind === 'OPEN' && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={t('leads.list.expired')}
                />
              )}
            </Stack>
          ),
      },
      {
        field: 'updatedAt',
        headerName: t('leads.list.updated'),
        width: 120,
        exportPath: 'updatedAt',
        valueFormatter: (value: string) =>
          value === '' ? '—' : formatCivilDate(value.slice(0, 10), locale),
      },
      {
        // R-4: the lead's half of the two-way link, once it exists.
        field: 'policyCode',
        headerName: t('leads.list.policy'),
        width: 150,
        exportPath: 'policyCode',
        renderCell: ({ row }) =>
          row.policyCode === '' ? (
            '—'
          ) : canSeePolicies ? (
            <Link
              href={links.insurance.policy(row.policyCode, { validAt: row.effectiveFrom })}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void navigate({
                  href: links.insurance.policy(row.policyCode, { validAt: row.effectiveFrom }),
                });
              }}
            >
              {row.policyCode}
            </Link>
          ) : (
            row.policyCode
          ),
      },
    ],
    [t, locale, today, showParent, canSeePolicies, canSeeGroupPolicies, navigate],
  );
  const exportColumns = useGridExportColumns(columns);

  const grid = (
    <InfiniteDataGrid
      rows={rows}
      list={list}
      columns={columns}
      ariaLabel={t('leads.list.ariaLabel')}
      emptyDescription={emptyMessage}
      errorDescription={t('leads.error.list')}
      sortKeys={LEAD_SORT_KEYS}
      {...(order === undefined ? {} : { order })}
      {...(onOrderChange === undefined ? {} : { onOrderChange })}
      onRowClick={(row) => {
        if (row.code === '') return;
        void navigate({ href: links.insurance.lead(row.code) });
      }}
    />
  );

  if (exportAs === undefined) return grid;
  return (
    <Stack spacing={1}>
      <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
        <ExportButton
          service="insurance"
          permission={p.exports.create}
          rowCount={list.isLoading ? undefined : rows.length}
          buildRequest={(format) => ({
            collection: 'leads',
            ...(exportAs.parent === undefined ? {} : { parent: exportAs.parent }),
            query: exportQueryOf(exportAs),
            columns: exportColumns,
            format,
            label: exportAs.label,
          })}
        />
      </Stack>
      {grid}
    </Stack>
  );
}
